// Copy of pinto/utils/appLogic/onboardingPreview.logic.ts; keep the two in sync.
import type { OnboardingPreview } from "../services/OnboardingPreviewService";

export type PreviewPlanTier = "basic" | "plus" | "pro";

/** Show the preview only when it has something real to show. */
export function shouldShowOnboardingPreview(preview: OnboardingPreview | null | undefined): preview is OnboardingPreview {
  if (!preview) return false;
  if (preview.mode === "own" || preview.mode === "nearby") return preview.deals.length > 0 && preview.dealCount > 0;
  if (preview.mode === "leads") return preview.leads.length > 0;
  return false;
}

/**
 * The plan to pre-pick after the preview, sized to what was shown: one store
 * (or no scanned store yet) fits Basic, 2–3 stores fit Plus (3 Walmarts), and
 * more than 3 is what Pro covers (up to 5 per chain).
 */
export function previewPlanTier(preview: OnboardingPreview | null | undefined): PreviewPlanTier {
  if (preview?.mode === "nearby") {
    if (preview.stores.length > 3) return "pro";
    if (preview.stores.length > 1) return "plus";
  }
  return "basic";
}

/**
 * Walmart stores to pre-pick on the store step: the previewed stores, in
 * preview order, that are in the nearby list, up to the plan's limit.
 */
export function previewStoresToPrepick(
  preview: OnboardingPreview | null | undefined,
  nearbyStoreIds: string[],
  limit: number,
): string[] {
  if (!preview || (preview.mode !== "own" && preview.mode !== "nearby") || limit <= 0) return [];
  const available = new Set(nearbyStoreIds);
  const picked: string[] = [];
  for (const store of preview.stores) {
    if (available.has(store.storeId) && !picked.includes(store.storeId)) picked.push(store.storeId);
    if (picked.length >= limit) break;
  }
  return picked;
}

/** "1616 W Henderson St, Cleburne, TX 76033" → "1616 W Henderson St". */
export function shortStoreAddress(address: string): string {
  const first = address.split(",")[0]?.trim() ?? "";
  return first.replace(/\s+Ste\s+\S+$/i, "");
}

/**
 * Card store line, like the feed's "Coconut Creek - #1916": the city when the
 * address carries one, otherwise the street.
 */
export function previewStoreLabel(address: string, storeId: string): string {
  const parts = address.split(",").map((part) => part.trim()).filter(Boolean);
  const place = parts.length >= 2 ? parts[1] : shortStoreAddress(address);
  return `${place} - #${storeId}`;
}

export function formatPreviewPrice(value: number): string {
  return `$${value.toFixed(2)}`;
}

const PREVIEW_RETAILER_NAMES: Record<string, string> = { walmart: "Walmart", home_depot: "Home Depot" };

/**
 * What a user walks away from by skipping the plan for the free online tier,
 * read from the store preview they just saw. Null when there is no real number
 * to show, so the confirm falls back to the generic copy.
 */
export type FreeSkipLoss = { count: number; retailer: string };

export function freeSkipLoss(preview: OnboardingPreview | null | undefined): FreeSkipLoss | null {
  if (!preview || (preview.mode !== "own" && preview.mode !== "nearby") || preview.dealCount <= 0) return null;
  return { count: preview.dealCount, retailer: PREVIEW_RETAILER_NAMES[preview.retailer] ?? "store" };
}
